import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import {
  addNotification,
  appendNotifications,
  markAllReadInState,
  markReadInState,
  setNotifications,
} from '../store/slices/notificationSlice';
import { disconnectRealtime, subscribeToNotifications, subscribeToRealtimeEvent } from '../services/socket';
import { changePassword } from '../services/api';
import { logoutUser } from '../services/api';
import { getMyNotifications, markAllNotificationsRead, markNotificationRead } from '../services/api';
import { requestBrowserNotificationPermission, showBrowserNotification } from '../services/browserNotifications';
import {
  shouldShowDesktopNotification,
  startNotificationCoordinator,
} from '../services/notificationCoordinator';
import { playNotificationSound, unlockNotificationSound } from '../services/notificationSound';
import { setAlert } from '../store/slices/alertSlice';
import { addToast } from '../store/slices/toastSlice';
import { useBrand } from '../context/BrandContext';

const NOTIFICATION_PAGE_SIZE = 10;

const Header = ({ sidebarCollapsed = false, onToggleSidebar }) => {
  const { brand } = useBrand();
  const {
    notifications,
    unreadCount,
    currentPage,
    totalPages,
    totalCount,
  } = useSelector((state) => state.notifications);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [desktopPermission, setDesktopPermission] = useState(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    if (!window.isSecureContext) return 'insecure';
    return Notification.permission;
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  useEffect(() => {
    setNotificationsLoading(true);
    getMyNotifications({ page: 1, limit: NOTIFICATION_PAGE_SIZE })
      .then(({ data }) => {
        dispatch(setNotifications({
          notifications: data.notifications || [],
          unreadCount: data.unreadCount || 0,
          currentPage: data.currentPage || 1,
          totalPages: data.totalPages || 1,
          totalCount: data.totalCount || 0,
        }));
        setNotificationsError('');
      })
      .catch(() => setNotificationsError('Unable to load notifications.'))
      .finally(() => setNotificationsLoading(false));
  }, [dispatch]);

  useEffect(() => {
    const stopCoordinator = startNotificationCoordinator();

    return () => stopCoordinator?.();
  }, []);

  useEffect(() => {
    const unlockSound = () => {
      unlockNotificationSound();
    };

    window.addEventListener('click', unlockSound, { once: true });
    window.addEventListener('keydown', unlockSound, { once: true });

    return () => {
      window.removeEventListener('click', unlockSound);
      window.removeEventListener('keydown', unlockSound);
    };
  }, []);

  useEffect(() => {
    const handleNewNotification = (notification) => {
      dispatch(addNotification(notification));

      dispatch(addToast({
        title: notification?.title || 'New notification',
        message: notification?.message,
        refNo: notification?.refNo,
      }));
      playNotificationSound();

      if (shouldShowDesktopNotification()) {
        showBrowserNotification({
          title: notification?.title || 'Incident notification',
          message: notification?.message,
          refNo: notification?.refNo,
        }).then((status) => {
          if (status) setDesktopPermission(status === 'shown' ? 'granted' : status);
        });
      }
    };

    const unsubscribe = subscribeToNotifications(handleNewNotification);

    return () => {
      unsubscribe?.();
    };
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = subscribeToRealtimeEvent('users:online', (payload) => {
      setOnlineCount(payload?.count || 0);
    });

    return () => unsubscribe?.();
  }, []);

  const handleLogout = async () => {
    setShowUserMenu(false);
    try {
      await logoutUser();
    } catch (error) {
      // Local logout should still continue if the server is unavailable.
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    disconnectRealtime();
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?._id || notification.isRead) return;
    dispatch(markReadInState(notification._id));
    try {
      await markNotificationRead(notification._id);
    } catch (error) {
      getMyNotifications({ page: 1, limit: NOTIFICATION_PAGE_SIZE })
        .then(({ data }) => {
          dispatch(setNotifications({
            notifications: data.notifications || [],
            unreadCount: data.unreadCount || 0,
            currentPage: data.currentPage || 1,
            totalPages: data.totalPages || 1,
            totalCount: data.totalCount || 0,
          }));
        })
        .catch(() => {});
    }
  };

  const handleMarkAllRead = async () => {
    dispatch(markAllReadInState());
    try {
      await markAllNotificationsRead();
    } catch (error) {
      getMyNotifications({ page: 1, limit: NOTIFICATION_PAGE_SIZE })
        .then(({ data }) => {
          dispatch(setNotifications({
            notifications: data.notifications || [],
            unreadCount: data.unreadCount || 0,
            currentPage: data.currentPage || 1,
            totalPages: data.totalPages || 1,
            totalCount: data.totalCount || 0,
          }));
        })
        .catch(() => {});
    }
  };

  const handleLoadMoreNotifications = async () => {
    if (notificationsLoading || currentPage >= totalPages) return;

    setNotificationsLoading(true);
    try {
      const { data } = await getMyNotifications({
        page: currentPage + 1,
        limit: NOTIFICATION_PAGE_SIZE,
      });
      dispatch(appendNotifications({
        notifications: data.notifications || [],
        unreadCount: data.unreadCount || 0,
        currentPage: data.currentPage || currentPage + 1,
        totalPages: data.totalPages || totalPages,
        totalCount: data.totalCount || totalCount,
      }));
      setNotificationsError('');
    } catch (error) {
      setNotificationsError('Unable to load more notifications.');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleNotificationBellClick = async () => {
    unlockNotificationSound();
    if (typeof window !== 'undefined' && 'Notification' in window && window.isSecureContext) {
      setDesktopPermission(Notification.permission);
    }
  };

  const handleEnableDesktopNotifications = async () => {
    unlockNotificationSound();
    const permission = await requestBrowserNotificationPermission();
    setDesktopPermission(permission);

    if (permission === 'granted') {
      dispatch(setAlert({
        type: 'success',
        message: 'Desktop notifications enabled.',
      }));
      return;
    }

    if (permission === 'insecure') {
      dispatch(setAlert({
        type: 'info',
        message: 'Desktop notifications need HTTPS or localhost.',
      }));
    } else if (permission === 'denied') {
      dispatch(setAlert({
        type: 'info',
        message: 'Desktop notifications are blocked in browser settings.',
      }));
    }
  };

  const getDesktopNotificationLabel = () => {
    if (desktopPermission === 'granted') return 'Desktop alerts on';
    if (desktopPermission === 'denied') return 'Desktop alerts blocked';
    if (desktopPermission === 'insecure') return 'Needs HTTPS';
    if (desktopPermission === 'unsupported') return 'Not supported';
    return 'Enable desktop alerts';
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const submitPasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      dispatch(setAlert({ type: 'danger', message: 'Enter all password fields.' }));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      dispatch(setAlert({ type: 'danger', message: 'New password and confirm password do not match.' }));
      return;
    }

    try {
      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordModal(false);
      dispatch(setAlert({ type: 'success', message: 'Password changed successfully.' }));
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Password change failed.',
      }));
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPassword({ currentPassword: false, newPassword: false, confirmPassword: false });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const passwordStrength = passwordData.newPassword.length >= 8
    ? 'Strong'
    : passwordData.newPassword.length >= 4
      ? 'Fair'
      : 'Weak';
  const displayTotalCount = Math.max(totalCount, notifications.length);
  const hasMoreNotifications = notifications.length < displayTotalCount;

  return (
    <header className={`app-header ${sidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="app-header-title">
        <button
          type="button"
          className="app-sidebar-toggle"
          onClick={onToggleSidebar}
          title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
          aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
        >
          <i className="bi bi-list" aria-hidden="true"></i>
        </button>
        <div className="welcome-copy">
          <span>{getGreeting()}, {user?.name || user?.username || 'Guest'}</span>
          <small>Welcome back to {brand.productName}</small>
        </div>
      </div>

      <div className="app-header-actions">
        <button
          className="header-icon-btn online-users-btn"
          type="button"
          title={`${onlineCount} users online`}
          aria-label={`${onlineCount} users online`}
        >
          <i className="bi bi-broadcast" aria-hidden="true"></i>
          <span className="online-users-count">{onlineCount}</span>
        </button>

        <div className="dropdown notification-dropdown">
          <button
            className="header-icon-btn"
            type="button"
            id="notificationDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title="Notifications"
            onClick={handleNotificationBellClick}
          >
            <i className="bi bi-bell" aria-hidden="true"></i>
            {unreadCount > 0 && (
              <span className="notification-count">{unreadCount}</span>
            )}
          </button>
          <ul className="dropdown-menu dropdown-menu-end notification-menu" aria-labelledby="notificationDropdown">
            <li className="notification-menu-header">
              <div>
                <strong>Notifications</strong>
                <span>{unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}</span>
              </div>
              <button type="button" onClick={handleMarkAllRead} disabled={unreadCount === 0}>
                Mark all read
              </button>
            </li>
            <li>
              <div className="notification-menu-list">
                {notifications.length > 0 ? (
                  notifications.map((notif, index) => (
                    <button
                      key={notif._id || index}
                      type="button"
                      className={`dropdown-item notification-item ${notif.isRead ? 'read' : 'unread'}`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      <span>{notif.title || 'Notification'}</span>
                      <small>{notif.message}</small>
                      {notif.refNo && <em>{notif.refNo}</em>}
                    </button>
                  ))
                ) : (
                  <div className="notification-empty">
                    {notificationsLoading ? 'Loading notifications...' : 'No notifications yet'}
                  </div>
                )}
              </div>
            </li>
            {notificationsError && (
              <li className="notification-menu-error">{notificationsError}</li>
            )}
            <li className="notification-menu-footer">
              <span>
                {displayTotalCount > 0 ? `${notifications.length} of ${displayTotalCount}` : ''}
              </span>
              <div className="notification-menu-footer-actions">
                <button
                  type="button"
                  onClick={handleEnableDesktopNotifications}
                  disabled={['granted', 'insecure', 'unsupported'].includes(desktopPermission)}
                >
                  {getDesktopNotificationLabel()}
                </button>
                {hasMoreNotifications && (
                  <button
                    type="button"
                    onClick={handleLoadMoreNotifications}
                    disabled={notificationsLoading}
                  >
                    {notificationsLoading ? 'Loading...' : 'Load more'}
                  </button>
                )}
              </div>
            </li>
          </ul>
        </div>

        <div className="dropdown">
          <button
            type="button"
            className="header-avatar-btn"
            id="userMenuDropdown"
            aria-expanded={showUserMenu}
            title="User menu"
            onClick={() => setShowUserMenu((value) => !value)}
          >
            {(user?.name || user?.username || 'G').charAt(0).toUpperCase()}
          </button>
          <ul
            className={`dropdown-menu dropdown-menu-end user-menu ${showUserMenu ? 'show' : ''}`}
            aria-labelledby="userMenuDropdown"
          >
            <li className="user-menu-header">
              <strong>{user?.name || user?.username || 'Guest'}</strong>
              <span>{user?.role || 'User'}</span>
            </li>
            <li><hr className="dropdown-divider" /></li>
            <li>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setShowUserMenu(false);
                  setShowPasswordModal(true);
                }}
              >
                <i className="bi bi-key" aria-hidden="true"></i>
                Change Password
              </button>
            </li>
            <li>
              <button type="button" className="dropdown-item text-danger" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right" aria-hidden="true"></i>
                Logout
              </button>
            </li>
          </ul>
        </div>
      </div>

      {showPasswordModal && createPortal(
        <>
        <div className="modal fade show d-block password-modal" tabIndex="-1" style={{ zIndex: 2060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">Change Password</h5>
                  <p className="password-modal-subtitle">Update your account password securely.</p>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closePasswordModal}
                ></button>
              </div>
              <div className="modal-body">
                {[
                  ['currentPassword', 'Current Password', 'bi-lock'],
                  ['newPassword', 'New Password', 'bi-shield-lock'],
                  ['confirmPassword', 'Confirm Password', 'bi-check2-circle'],
                ].map(([field, label, icon]) => (
                  <label className="password-field" key={field}>
                    <span>{label}</span>
                    <div className="password-input-wrap">
                      <i className={`bi ${icon}`} aria-hidden="true"></i>
                      <input
                        type={showPassword[field] ? 'text' : 'password'}
                        name={field}
                        value={passwordData[field]}
                        onChange={handlePasswordChange}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility(field)}
                        title={showPassword[field] ? 'Hide password' : 'Show password'}
                        aria-label={showPassword[field] ? 'Hide password' : 'Show password'}
                      >
                        <i className={`bi ${showPassword[field] ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true"></i>
                      </button>
                    </div>
                  </label>
                ))}
                <div className="password-hint">
                  <span>Strength: {passwordStrength}</span>
                  <span>Use at least 8 characters.</span>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="password-secondary-btn"
                  onClick={closePasswordModal}
                >
                  Cancel
                </button>
                <button type="button" className="password-primary-btn" onClick={submitPasswordChange}>
                  <i className="bi bi-check2" aria-hidden="true"></i>
                  Update Password
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 2055 }}></div>
        </>,
        document.body
      )}
    </header>
  );
};

export default Header;
